import { Component, forwardRef, Input, ViewEncapsulation } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { isDefined } from '@fi-shared/utils/is-defined';
import { SiteMapService } from '../../../site-map/site-map.service';
import { IWidgetSettingsModel } from '@fi-shared/widget/widget-settings.model';
import { ControlValueAccessorBase } from '@fi-shared/control-value-accessor-validator-base';
import { WidgetSharedService } from '@fi-shared/widget/widget-shared.service';
import { ILightWeightObject } from '@fi-shared/models';
import { BehaviorSubject } from 'rxjs';

@Component({
    selector: 'fi-widget-sitemap-edit',
    template: `
        @if (ngModel) {
          <div class="edit-site-map-blocks">
            <fi-select-search
              [items]="siteMaps$ | async"
              [(ngModel)]="ngModel.SiteMapId"
              (ngModelChange)="siteMapChange($event)"
              [label]="'sitemap.sitemap_to_embed' | fiAppText"
              [disabled]="disabled$ | async"
              />
              <div [textContent]="'widget_settings_options_title' | fiAppText" class="options-header"></div>
              @if (isWidgetMode) {
                <div>
                  <mat-checkbox class="fi-rounded-checkbox" [(ngModel)]="ngModel.DisplayContainer">{{
                    'widget.display_container' | fiAppText
                  }}</mat-checkbox>
                </div>
              }
              @if (isWidgetMode) {
                <div>
                  <mat-checkbox class="fi-rounded-checkbox" [(ngModel)]="ngModel.DisplayHeader">{{
                    'widget.display_header' | fiAppText
                  }}</mat-checkbox>
                </div>
              }
              <div>
                <mat-checkbox
                  class="fi-rounded-checkbox"
                  [(ngModel)]="ngModel.displayTitle"
                  (ngModelChange)="settingsPropertyOnChange({ skipReloadData: true })"
                  >
                  {{
                  (isWidgetMode ? 'sitemap.widget.display_sitemap_title' : 'sitemap.widget.display_title')
                  | fiAppText
                  }}
                </mat-checkbox>
              </div>
              <div>
                <mat-checkbox
                  class="fi-rounded-checkbox"
                  [(ngModel)]="ngModel.displayNumbering"
                  (ngModelChange)="settingsPropertyOnChange({ skipReloadData: true })"
                  >
                  {{ 'sitemap.widget.display_numbering' | fiAppText }}
                </mat-checkbox>
              </div>
              <div>
                <mat-checkbox
                  class="fi-rounded-checkbox"
                  [(ngModel)]="ngModel.displayFiles"
                  (ngModelChange)="settingsPropertyOnChange({ skipReloadData: true })"
                  >
                  {{ 'sitemap.widget.display_files' | fiAppText }}
                </mat-checkbox>
              </div>
              <div>
                <mat-checkbox
                  class="fi-rounded-checkbox"
                  [(ngModel)]="ngModel.indicateUnreadItems"
                  (ngModelChange)="settingsPropertyOnChange({ skipReloadData: true })"
                  >
                  {{ 'sitemap.widget.indicate_unread_items' | fiAppText }}
                </mat-checkbox>
              </div>
              <div>
                <mat-checkbox
                  class="fi-rounded-checkbox"
                  [(ngModel)]="ngModel.displayThumbnails"
                  (ngModelChange)="settingsPropertyOnChange({ skipReloadData: true })"
                  >
                  {{ 'sitemap.widget.display_thumbnails' | fiAppText }}
                </mat-checkbox>
              </div>
            </div>
          }
        `,
    styleUrls: ['./edit.component.scss'],
    encapsulation: ViewEncapsulation.None,
    host: { class: 'fi-widget-sitemap-edit' },
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            multi: true,
            useExisting: forwardRef(() => WidgetSitemapEditComponent),
        },
    ],
    standalone: false,
})
export class WidgetSitemapEditComponent extends ControlValueAccessorBase<IWidgetSettingsModel> {
    @Input() isWidgetMode: boolean = false;
    siteMaps$ = new BehaviorSubject<ILightWeightObject[]>([]);
    disabled$ = new BehaviorSubject<boolean>(false);

    constructor(
        private siteMapService: SiteMapService,
        private widgetSharedService: WidgetSharedService,
    ) {
        super();
    }

    ngOnInit() {
        if (!isDefined(this.ngModel)) {
            this.ngModel = {};
            this.doPropagateChanges();
        }

        this.siteMapService.getLightWeightSiteMaps().then((response: ILightWeightObject[]) => {
            this.siteMaps$.next(response);
            const currentSiteMapId = this.ngModel.SiteMapId;
            if (currentSiteMapId && !response.find((siteMap) => siteMap.Id === currentSiteMapId)) {
                // current user has no access to sitemap embed to page, fetch only current sitemap and disable dropdown
                this.siteMapService.getLightWeightSiteMap(currentSiteMapId).then((siteMap: ILightWeightObject) => {
                    if (siteMap) {
                        this.siteMaps$.next([siteMap]);
                        this.disabled$.next(true);
                    }
                });
            }
        });

        if (!isDefined(this.ngModel.displayTitle)) {
            this.ngModel.displayTitle = true;
        }

        if (!isDefined(this.ngModel.displayNumbering)) {
            this.ngModel.displayNumbering = true;
        }

        if (!isDefined(this.ngModel.displayFiles)) {
            this.ngModel.displayFiles = true;
        }
    }

    siteMapChange($event) {
        this.doPropagateChanges();
        this.settingsPropertyOnChange();
    }

    public settingsPropertyOnChange(data?: { skipReloadData?: boolean }) {
        this.widgetSharedService.raiseWidgetSettingsChanged(this.ngModel, data);
    }
}
